// Library Methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import HubSection from "src/pages/EmrConnections/HubSection/HubSection";
import SystemCard from "src/pages/EmrConnections/SystemCard/SystemCard";
import JobRow, { JobStatus } from "src/pages/EmrConnections/JobRow/JobRow";
import {
  CalendarIcon,
  ChevronLeftIcon,
  FolderIcon,
  MachineIcon,
  ReportIcon,
  ServerIcon,
} from "src/assets/icons";
import {
  MockButton,
  mockCardBackground,
  mockTileBackground,
  noop,
} from "./PortalMocks";

// Utils
import { guideTarget } from "../Walkthrough/walkthroughState";

// Styles
import {
  getRequiredErrorColor,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

const target = (id?: string, inner?: string) =>
  id ? guideTarget(id, inner) : {};

type SectionKind = "worklist" | "machine" | "reports";

const SECTIONS: Record<
  SectionKind,
  {
    icon: ReactNode;
    title: string;
    description: string;
    addLabel: string;
    emptyText: string;
  }
> = {
  worklist: {
    icon: <CalendarIcon />,
    title: "Worklist",
    description:
      "Who is booked, so exams land on the right patient. From a PAC server, which can then be queried for those patients' exams, or from a CSV your EMR drops in a folder.",
    addLabel: "Add worklist",
    emptyText: "No worklist yet.",
  },
  machine: {
    icon: <MachineIcon />,
    title: "Machine",
    description:
      "A machine sending its exams straight to the hub, over DICOM or by exporting files to a folder.",
    addLabel: "Add machine",
    emptyText: "No machines yet.",
  },
  reports: {
    icon: <ReportIcon />,
    title: "RetinaLogik Exams",
    description:
      "Where finished RetinaLogik reports go back to the clinic: a PAC server, or a folder your EMR imports from.",
    addLabel: "Add destination",
    emptyText: "RetinaLogik reports are not sent anywhere yet.",
  },
};

export const MockHubSection = ({
  kind,
  sectionTarget,
  addTarget,
  children,
}: {
  kind: SectionKind;
  sectionTarget?: string;
  addTarget?: string;
  children?: ReactNode;
}) => {
  const { t } = useTranslation();
  const section = SECTIONS[kind];

  return (
    <Box {...target(sectionTarget)}>
      <Box {...target(addTarget, "button")}>
        <HubSection
          id={`guide-${kind}`}
          icon={section.icon}
          title={t(section.title)}
          description={t(section.description)}
          addLabel={t(section.addLabel)}
          canAdd
          onAdd={noop}
          isLoading={false}
          isEmpty={!children}
          emptyText={t(section.emptyText)}
        >
          {children}
        </HubSection>
      </Box>
    </Box>
  );
};

export const MockConnectionsHeader = ({
  backTarget,
  titleTarget,
  title,
}: {
  backTarget?: string;
  titleTarget?: string;
  title: string;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Box alignSelf="flex-start">
        <MockButton
          kind="link"
          startIcon={<ChevronLeftIcon />}
          targetId={backTarget}
        >
          {t("Back to Data Hub")}
        </MockButton>
      </Box>
      <Typography {...target(titleTarget)} fontSize="20px" fontWeight={700}>
        {title}
      </Typography>
    </>
  );
};

export type MockJob = {
  direction: "In" | "Out";
  title: string;
  description: string;
  status: JobStatus;
  badge?: string;
  statusTarget?: string;
};

export const MockSystemCard = ({
  icon,
  name,
  subtitle,
  canDelete = true,
  jobs,
  cardTarget,
  children,
}: {
  icon: "server" | "folder" | "machine";
  name: string;
  subtitle: string;
  canDelete?: boolean;
  jobs: Array<MockJob>;
  cardTarget?: string;
  children?: ReactNode;
}) => (
  <Box {...target(cardTarget)}>
    <SystemCard
      icon={
        icon === "server" ? (
          <ServerIcon width="20" height="20" aria-hidden="true" />
        ) : icon === "folder" ? (
          <FolderIcon />
        ) : (
          <MachineIcon />
        )
      }
      name={name}
      subtitle={subtitle}
      onEdit={noop}
      onDelete={canDelete ? noop : undefined}
    >
      {jobs.map((job) => (
        <Box
          key={job.title}
          {...target(job.statusTarget, ":scope > div > div > :last-child")}
        >
          <JobRow
            direction={job.direction}
            title={job.title}
            description={job.description}
            status={job.status}
            badge={job.badge}
          />
        </Box>
      ))}
      {children}
    </SystemCard>
  </Box>
);

export const MockCardGrid = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))",
      gap: "12px",
    }}
  >
    {children}
  </Box>
);

export const MockOutboxPanel = ({
  pending,
  sent,
  failed,
  panelTarget,
  viewTarget,
  failedTarget,
}: {
  pending: number;
  sent: number;
  failed: number;
  panelTarget?: string;
  viewTarget?: string;
  failedTarget?: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const counters = [
    { label: t("Pending"), value: pending },
    { label: t("Sent"), value: sent },
    {
      label: t("Failed"),
      value: failed,
      isError: true,
      targetId: failedTarget,
    },
  ];

  return (
    <Box
      {...target(panelTarget)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px",
        borderRadius: "4px",
        backgroundColor: mockTileBackground(theme),
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize="14px" fontWeight={600} color={mutedText}>
          {t("Report Outbox")}
        </Typography>
        <MockButton kind="cancel" small targetId={viewTarget}>
          {t("View")}
        </MockButton>
      </Box>
      <Box display="flex" gap="8px">
        {counters.map((counter) => (
          <Box key={counter.label} flex={1} {...target(counter.targetId)}>
            <Typography fontSize="12px" color={mutedText}>
              {counter.label}
            </Typography>
            <Typography
              fontSize="14px"
              fontWeight={600}
              color={
                counter.isError && counter.value > 0
                  ? getRequiredErrorColor(theme)
                  : undefined
              }
            >
              {counter.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const MockProblemsBox = ({
  boxTarget,
  itemTarget,
  connection,
  message,
  lastSeen,
}: {
  boxTarget?: string;
  itemTarget?: string;
  connection: string;
  message: string;
  lastSeen: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const errorColor = getRequiredErrorColor(theme);

  return (
    <Box
      {...target(boxTarget)}
      sx={{
        border: `1px solid ${errorColor}`,
        borderRadius: "8px",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        backgroundColor: mockCardBackground(theme),
      }}
    >
      <Typography fontSize="16px" fontWeight={700} color={errorColor}>
        {`${t("Problems")} (1)`}
      </Typography>
      <Typography fontSize="12px" color={mutedText}>
        {t(
          "These need fixing on the clinic's side. Each one clears by itself once the hub reports it working again."
        )}
      </Typography>
      <Box
        {...target(itemTarget)}
        sx={{
          backgroundColor:
            theme.palette.mode === "dark" ? "#24263A" : "rgba(0, 0, 0, 0.05)",
          borderRadius: "6px",
          padding: "8px 10px",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          fontSize: "13px",
        }}
      >
        <b>{connection}</b>
        <span>{message}</span>
        <Typography fontSize="12px" color={mutedText}>
          {`${t("Last seen")}: ${lastSeen} · ${t("Times seen")}: 3`}
        </Typography>
      </Box>
    </Box>
  );
};
